// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Return + Debit Note API client (Stage 6P.8).
//
// Thin axios wrappers around:
//   /api/erp/purchase-returns/*  (Purchase Return + Debit Note —
//                                 Stage 6P.8)
//
// Mirrors the supplierPaymentApi shape — the `unwrap` helper handles
// both the {data:{data:...}} envelope used by most list/create
// endpoints and the bare {data:...} shape used by preview / status /
// helper routes.
//
// Backend is the source of truth for ALL purchase return math:
//   return / debit-note number allocation (PRET-YYYYMMDD-NNNNNN /
//   PDN-YYYYMMDD-NNNNNN) via advisory locks, supplier resolution,
//   returnable-balance computation across GRN ↔ Purchase Bill,
//   ITC reversal posting, inventory OUTWARD movement, debit-note
//   voucher posting through the existing voucherPosting public
//   contract. Frontend mirrors for instant UX but never wins.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const PR_BASE = '/api/erp/purchase-returns';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const purchaseReturnApi = {
  // ── CRUD ─────────────────────────────────────────────────────
  list:    (params = {}) =>
    client.get(`${PR_BASE}`, { params }).then(unwrap),
  get:     (id) =>
    client.get(`${PR_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create:  (payload = {}) =>
    client.post(`${PR_BASE}`, payload).then(unwrap),
  update:  (id, payload = {}) =>
    client.put(`${PR_BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove:  (id) =>
    client.delete(`${PR_BASE}/${encodeURIComponent(id)}`).then(unwrap),

  // ── Lifecycle — preview / confirm / post / cancel ───────────
  preview: (id) =>
    client
      .get(`${PR_BASE}/${encodeURIComponent(id)}/preview`)
      .then(unwrap),
  confirm: (id, payload = {}) =>
    client
      .post(`${PR_BASE}/${encodeURIComponent(id)}/confirm`, payload)
      .then(unwrap),
  post:    (id, payload = {}) =>
    client
      .post(`${PR_BASE}/${encodeURIComponent(id)}/post`, payload)
      .then(unwrap),
  cancel:  (id, reason) =>
    client
      .post(`${PR_BASE}/${encodeURIComponent(id)}/cancel`, { reason })
      .then(unwrap),

  // ── Source-document drafting helpers ────────────────────────
  // Server-side "draft from X" endpoints — the server snapshots the
  // referenced GRN ledger row or Purchase Bill and pre-populates the
  // draft so the operator only adjusts qty / rate / remarks.
  createFromInventoryLedger: (ledgerId, payload = {}) =>
    client
      .post(
        `${PR_BASE}/from-inventory-ledger/${encodeURIComponent(ledgerId)}`,
        payload
      )
      .then(unwrap),
  createFromPurchaseBill:    (purchaseBillId, payload = {}) =>
    client
      .post(
        `${PR_BASE}/from-purchase-bill/${encodeURIComponent(purchaseBillId)}`,
        payload
      )
      .then(unwrap),

  // ── Returnable balance probe ─────────────────────────────────
  // Used by the form to keep the per-line return_balance_qty /
  // return_balance_sqmt / return_balance_total_length surface live
  // as the operator edits qty. Backend deducts prior active returns.
  getReturnableBalance: (params = {}) =>
    client
      .get(`${PR_BASE}/returnable-balance`, { params })
      .then(unwrap),

  // ── Reports / register ──────────────────────────────────────
  // ITC reversal register — drives /erp/purchase-returns/itc-reversals.
  listItcReversals: (params = {}) =>
    client
      .get(`${PR_BASE}/itc-reversals`, { params })
      .then(unwrap),
  // Posted-return register — drives /erp/purchase-returns/register.
  listRegister:     (params = {}) =>
    client
      .get(`${PR_BASE}/register`, { params })
      .then(unwrap)
};

export default { purchaseReturnApi };
